import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Save, Ban, RefreshCcw, Check, X, OctagonX, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

const STATUS_LABEL = { pending: "Ausstehend", aktiv: "Aktiv", beendet: "Beendet", storniert: "Storniert" };
const STATUS_CLS = {
  pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  aktiv: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  beendet: "bg-red-500/20 text-red-300 border-red-500/30",
  storniert: "bg-white/10 text-[#999] border-white/15",
};

const toDateInput = (iso) => (iso ? iso.slice(0, 10) : "");

const TIER_LABELS = { basic: "Basic", standard: "Standard", premium: "Premium" };

function SettingsCard({ settings, onSaved }) {
  const [f, setF] = useState({
    tiers: { ...settings.tiers }, min_term_months: settings.min_term_months, repairs_per_year: settings.repairs_per_year,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/protection-settings", {
        tiers: Object.fromEntries(Object.entries(f.tiers).map(([k, v]) => [k, Number(v)])),
        min_term_months: Number(f.min_term_months), repairs_per_year: Number(f.repairs_per_year),
      });
      onSaved(data);
      toast.success("Einstellungen gespeichert.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} data-testid="protection-settings-form" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 mb-8">
      <p className="text-sm font-medium text-white mb-4">Konditionen (gelten für neu abgeschlossene Schutzpakete)</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        {Object.keys(TIER_LABELS).map((tier) => (
          <div key={tier}>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">{TIER_LABELS[tier]} (€/Monat)</label>
            <input data-testid={`protection-settings-tier-${tier}`} type="number" min="0" step="0.5" value={f.tiers[tier] ?? 0}
              onChange={(e) => setF((p) => ({ ...p, tiers: { ...p.tiers, [tier]: e.target.value } }))} className={inputCls} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Mindestlaufzeit (Monate)</label>
          <input data-testid="protection-settings-term" type="number" min="1" step="1" value={f.min_term_months}
            onChange={(e) => setF((p) => ({ ...p, min_term_months: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Reparaturen / Jahr</label>
          <input data-testid="protection-settings-quota" type="number" min="1" step="1" value={f.repairs_per_year}
            onChange={(e) => setF((p) => ({ ...p, repairs_per_year: e.target.value }))} className={inputCls} />
        </div>
      </div>
      <button type="submit" disabled={saving} data-testid="protection-settings-save"
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-5 py-2.5 text-sm font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
        <Save size={16} /> {saving ? "Speichern…" : "Speichern"}
      </button>
    </form>
  );
}

function ModelTierMapping({ pricing, onUpdated }) {
  const setTier = async (p, tier) => {
    try {
      await api.patch(`/admin/protection-model-tier/${p.id}`, { protection_tier: tier });
      toast.success(`${p.model}: ${tier ? TIER_LABELS[tier] : "keine Stufe"}`);
      onUpdated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen."); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 mb-8">
      <p className="text-sm font-medium text-white mb-1">Preisstufen-Zuordnung je Modell</p>
      <p className="text-xs text-[#888] mb-4">Nur Modelle mit zugeordneter Stufe können ein Schutzpaket abschließen.</p>
      <div className="space-y-2">
        {pricing.map((p) => (
          <div key={p.id} data-testid={`protection-tier-row-${p.id}`} className="flex items-center justify-between gap-3 rounded-xl bg-white/5 px-4 py-2.5">
            <span className="text-sm text-[#ddd]">{p.model}</span>
            <select value={p.protection_tier || ""} onChange={(e) => setTier(p, e.target.value)}
              data-testid={`protection-tier-select-${p.id}`} className={`${inputCls} w-44 py-2`}>
              <option value="" className="bg-[#0a0a0a]">Keine Stufe</option>
              {Object.keys(TIER_LABELS).map((tier) => (
                <option key={tier} value={tier} className="bg-[#0a0a0a]">{TIER_LABELS[tier]}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

function TransferRequests({ requests, onResolved }) {
  const [priceInputs, setPriceInputs] = useState({});

  const approve = async (r) => {
    try {
      await api.post(`/admin/protection-transfers/${r.id}/approve`, {
        new_monthly_price: priceInputs[r.id] ? Number(priceInputs[r.id]) : null,
      });
      toast.success("Übertragung genehmigt.");
      onResolved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehlgeschlagen."); }
  };

  const reject = async (r) => {
    try {
      await api.post(`/admin/protection-transfers/${r.id}/reject`);
      toast.success("Übertragung abgelehnt.");
      onResolved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehlgeschlagen."); }
  };

  const open = requests.filter((r) => r.status === "offen");
  if (open.length === 0) return null;

  return (
    <div className="mb-8">
      <p className="text-sm font-medium text-white mb-3">Offene Geräteübertragungen</p>
      <div className="space-y-3">
        {open.map((r) => (
          <div key={r.id} data-testid={`protection-transfer-${r.id}`} className="rounded-2xl border border-sky-500/20 bg-sky-500/5 p-5">
            <p className="text-sm text-white">{r.old_device} <span className="text-[#888]">→</span> {r.new_device}</p>
            <p className="text-xs text-[#888] mt-1">Neue Seriennummer: {r.new_device_serial}</p>
            {r.note && <p className="text-xs text-[#999] mt-1 italic">„{r.note}"</p>}
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <input data-testid={`protection-transfer-price-${r.id}`} type="number" min="0" step="0.5" placeholder="Neuer Preis (optional)"
                value={priceInputs[r.id] || ""} onChange={(e) => setPriceInputs((p) => ({ ...p, [r.id]: e.target.value }))}
                className={`${inputCls} w-48`} />
              <button onClick={() => approve(r)} data-testid={`protection-transfer-approve-${r.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#050505] px-4 py-2 text-xs font-medium hover:bg-[#d1d5db] transition-colors">
                <Check size={14} /> Genehmigen
              </button>
              <button onClick={() => reject(r)} data-testid={`protection-transfer-reject-${r.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 text-[#ccc] px-4 py-2 text-xs hover:text-white transition-colors">
                <X size={14} /> Ablehnen
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProtectionView() {
  const [settings, setSettings] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceForm, setDeviceForm] = useState({ new_device: "", new_device_serial: "", new_monthly_price: "" });
  const [savingDevice, setSavingDevice] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({});
  const [savingContact, setSavingContact] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c, t, p] = await Promise.all([
        api.get("/admin/protection-settings"),
        api.get("/admin/protection-contracts"),
        api.get("/admin/protection-transfers"),
        api.get("/pricing"),
      ]);
      setSettings(s.data); setContracts(c.data); setTransfers(t.data); setPricing(p.data);
    } catch (e) { toast.error("Schutzpakete konnten nicht geladen werden."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelContract = async (c) => {
    if (!window.confirm(`Vertrag ${c.contract_number} fristgerecht kündigen?`)) return;
    try {
      const { data } = await api.post(`/admin/protection-contracts/${c.id}/cancel`);
      toast.success(`Gekündigt zum ${new Date(data.cancel_effective_date).toLocaleDateString("de-DE")}.`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kündigung fehlgeschlagen."); }
  };

  const stopContract = async (c) => {
    if (!window.confirm(
      `Vertrag ${c.contract_number} SOFORT stoppen?\n\nDie automatische Abbuchung wird sofort beendet, keine anteilige Rückerstattung. Der Kunde wird per E-Mail informiert. Dies kann nicht rückgängig gemacht werden.`
    )) return;
    try {
      await api.post(`/admin/protection-contracts/${c.id}/stop`);
      toast.success("Schutzpaket sofort gestoppt. Kunde wurde informiert.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sofortiges Stoppen fehlgeschlagen."); }
  };

  const voidContract = async (c) => {
    if (!window.confirm(`Unbezahlte Buchung ${c.contract_number} stornieren?\n\nDer Kunde wird per E-Mail informiert, es wurde keine Zahlung eingezogen.`)) return;
    try {
      await api.post(`/admin/protection-contracts/${c.id}/storno`);
      toast.success("Buchung storniert. Kunde wurde informiert.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Stornierung fehlgeschlagen."); }
  };

  const sendCertificate = async (c) => {
    try {
      await api.post(`/admin/protection-contracts/${c.id}/send-certificate`);
      toast.success("Zertifikat per E-Mail verschickt.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Versand fehlgeschlagen."); }
  };

  const startDeviceEdit = (c) => {
    setEditingDevice(c.id);
    setDeviceForm({ new_device: c.device, new_device_serial: "", new_monthly_price: "" });
  };

  const saveDeviceChange = async (c) => {
    if (!deviceForm.new_device_serial.trim()) { toast.error("Bitte neue Seriennummer angeben."); return; }
    setSavingDevice(true);
    try {
      await api.post(`/admin/protection-contracts/${c.id}/transfer-device`, {
        new_device: deviceForm.new_device, new_device_serial: deviceForm.new_device_serial,
        new_monthly_price: deviceForm.new_monthly_price ? Number(deviceForm.new_monthly_price) : null,
      });
      toast.success("Gerät übertragen, Kunde wurde per E-Mail informiert.");
      setEditingDevice(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Übertragung fehlgeschlagen."); }
    finally { setSavingDevice(false); }
  };

  const startContactEdit = (c) => {
    setEditingContact(c.id);
    setContactForm({
      first_name: c.first_name, last_name: c.last_name, email: c.email, phone: c.phone,
      street: c.street, zip: c.zip, city: c.city,
      start_date: toDateInput(c.start_date), min_term_end_date: toDateInput(c.min_term_end_date),
      next_billing_date: toDateInput(c.next_billing_date),
    });
  };

  const saveContactEdit = async (c) => {
    setSavingContact(true);
    try {
      await api.patch(`/admin/protection-contracts/${c.id}`, contactForm);
      toast.success("Vertragsdaten aktualisiert.");
      setEditingContact(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen."); }
    finally { setSavingContact(false); }
  };

  if (loading || !settings) return <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>;

  return (
    <div data-testid="protection-view">
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-2 flex items-center gap-3">
        <ShieldCheck size={32} /> Schutzpakete
      </h1>
      <p className="text-sm text-[#888] mb-8 max-w-2xl">Monatliche Reparatur-Flatrate-Verträge für Kunden – Konditionen, laufende Verträge und Geräteübertragungen.</p>

      <SettingsCard settings={settings} onSaved={setSettings} />
      <ModelTierMapping pricing={pricing} onUpdated={load} />
      <TransferRequests requests={transfers} onResolved={load} />

      {contracts.length === 0 ? (
        <div data-testid="protection-contracts-empty" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-16 text-center">
          <p className="text-[#888]">Noch keine Schutzpakete abgeschlossen.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="protection-contracts-list">
          {contracts.map((c) => (
            <div key={c.id} data-testid={`protection-contract-${c.id}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-white">{c.contract_number}</span>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${STATUS_CLS[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                    {c.cancel_effective_date && (
                      <span className="text-xs px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
                        Endet {new Date(c.cancel_effective_date).toLocaleDateString("de-DE")}
                      </span>
                    )}
                  </div>
                  <p className="text-white mt-2">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-[#888] mt-1">{c.email} · {c.device}</p>
                </div>
                <div className="lg:col-span-4 text-sm text-[#999]">
                  <p>{c.monthly_price.toFixed(2)} € / Monat</p>
                  <p className="mt-1">Kontingent: {c.repairs_used_this_year}/{c.repairs_per_year} genutzt</p>
                  <p className="mt-1">{c.status === "aktiv" ? `Nächste Abbuchung: ${c.next_billing_date ? new Date(c.next_billing_date).toLocaleDateString("de-DE") : "–"}` : ""}</p>
                </div>
                <div className="lg:col-span-3 flex lg:justify-end gap-2 flex-wrap">
                  {c.status === "pending" && (
                    <button onClick={() => voidContract(c)} data-testid={`protection-contract-storno-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 text-red-300 px-3 py-2 text-xs hover:bg-red-500/10 transition-colors">
                      <Ban size={13} /> Stornieren
                    </button>
                  )}
                  {c.status === "aktiv" && !c.cancel_effective_date && (
                    <button onClick={() => cancelContract(c)} data-testid={`protection-contract-cancel-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 text-red-300 px-3 py-2 text-xs hover:bg-red-500/10 transition-colors">
                      <Ban size={13} /> Kündigen
                    </button>
                  )}
                  {c.status === "aktiv" && (
                    <button onClick={() => stopContract(c)} data-testid={`protection-contract-stop-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 px-3 py-2 text-xs hover:bg-red-500/20 transition-colors font-medium">
                      <OctagonX size={13} /> Sofort stoppen
                    </button>
                  )}
                  {c.status === "aktiv" && (
                    <button onClick={() => startDeviceEdit(c)} data-testid={`protection-contract-transfer-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 text-[#ccc] px-3 py-2 text-xs hover:text-white transition-colors">
                      <RefreshCcw size={13} /> Gerät ändern
                    </button>
                  )}
                  <button onClick={() => startContactEdit(c)} data-testid={`protection-contract-edit-${c.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 text-[#ccc] px-3 py-2 text-xs hover:text-white transition-colors">
                    <Pencil size={13} /> Bearbeiten
                  </button>
                  {(c.status === "aktiv" || c.status === "beendet") && (
                    <button onClick={() => sendCertificate(c)} data-testid={`protection-contract-certificate-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 text-emerald-300 px-3 py-2 text-xs hover:bg-emerald-500/10 transition-colors">
                      <ShieldCheck size={13} /> Zertifikat senden
                    </button>
                  )}
                </div>
              </div>

              {editingContact === c.id && (
                <div className="mt-4 pt-4 border-t border-white/10" data-testid={`protection-contract-edit-form-${c.id}`}>
                  <p className="text-xs font-mono uppercase tracking-widest text-[#888] mb-3">Kundendaten</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <input value={contactForm.first_name || ""} onChange={(e) => setContactForm((p) => ({ ...p, first_name: e.target.value }))}
                      placeholder="Vorname" data-testid={`protection-contract-edit-first-name-${c.id}`} className={inputCls} />
                    <input value={contactForm.last_name || ""} onChange={(e) => setContactForm((p) => ({ ...p, last_name: e.target.value }))}
                      placeholder="Nachname" data-testid={`protection-contract-edit-last-name-${c.id}`} className={inputCls} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <input value={contactForm.email || ""} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder="E-Mail" data-testid={`protection-contract-edit-email-${c.id}`} className={inputCls} />
                    <input value={contactForm.phone || ""} onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="Telefon" data-testid={`protection-contract-edit-phone-${c.id}`} className={inputCls} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                    <input value={contactForm.street || ""} onChange={(e) => setContactForm((p) => ({ ...p, street: e.target.value }))}
                      placeholder="Straße & Hausnummer" data-testid={`protection-contract-edit-street-${c.id}`} className={inputCls} />
                    <input value={contactForm.zip || ""} onChange={(e) => setContactForm((p) => ({ ...p, zip: e.target.value }))}
                      placeholder="PLZ" data-testid={`protection-contract-edit-zip-${c.id}`} className={inputCls} />
                    <input value={contactForm.city || ""} onChange={(e) => setContactForm((p) => ({ ...p, city: e.target.value }))}
                      placeholder="Ort" data-testid={`protection-contract-edit-city-${c.id}`} className={inputCls} />
                  </div>
                  <p className="text-xs font-mono uppercase tracking-widest text-[#888] mb-3">Vertragsdaten (nur für Korrekturen)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                    <div>
                      <label className="block text-[11px] text-[#777] mb-1.5">Vertragsbeginn</label>
                      <input type="date" value={contactForm.start_date || ""} onChange={(e) => setContactForm((p) => ({ ...p, start_date: e.target.value }))}
                        data-testid={`protection-contract-edit-start-${c.id}`} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-[11px] text-[#777] mb-1.5">Mindestlaufzeit-Ende</label>
                      <input type="date" value={contactForm.min_term_end_date || ""} onChange={(e) => setContactForm((p) => ({ ...p, min_term_end_date: e.target.value }))}
                        data-testid={`protection-contract-edit-min-term-${c.id}`} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-[11px] text-[#777] mb-1.5">Nächste Abbuchung</label>
                      <input type="date" value={contactForm.next_billing_date || ""} onChange={(e) => setContactForm((p) => ({ ...p, next_billing_date: e.target.value }))}
                        data-testid={`protection-contract-edit-next-billing-${c.id}`} className={inputCls} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => saveContactEdit(c)} disabled={savingContact} data-testid={`protection-contract-edit-save-${c.id}`}
                      className="rounded-full bg-white text-[#050505] px-6 py-2.5 text-xs font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
                      {savingContact ? "Speichern…" : "Speichern"}
                    </button>
                    <button onClick={() => setEditingContact(null)} data-testid={`protection-contract-edit-cancel-${c.id}`}
                      className="px-4 rounded-full border border-white/15 text-[#ccc] text-xs hover:text-white transition-colors">Abbrechen</button>
                  </div>
                </div>
              )}

              {editingDevice === c.id && (
                <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid={`protection-contract-transfer-form-${c.id}`}>
                  <select value={deviceForm.new_device} onChange={(e) => setDeviceForm((p) => ({ ...p, new_device: e.target.value }))}
                    data-testid={`protection-contract-transfer-device-${c.id}`} className={inputCls}>
                    {pricing.map((p) => <option key={p.id} value={p.model} className="bg-[#0a0a0a]">{p.model}</option>)}
                  </select>
                  <input value={deviceForm.new_device_serial} onChange={(e) => setDeviceForm((p) => ({ ...p, new_device_serial: e.target.value }))}
                    placeholder="Neue Seriennummer" data-testid={`protection-contract-transfer-serial-${c.id}`} className={inputCls} />
                  <input type="number" min="0" step="0.5" value={deviceForm.new_monthly_price} onChange={(e) => setDeviceForm((p) => ({ ...p, new_monthly_price: e.target.value }))}
                    placeholder="Neuer Preis (optional)" data-testid={`protection-contract-transfer-price-${c.id}`} className={inputCls} />
                  <div className="flex gap-2">
                    <button onClick={() => saveDeviceChange(c)} disabled={savingDevice} data-testid={`protection-contract-transfer-save-${c.id}`}
                      className="flex-1 rounded-full bg-white text-[#050505] py-2.5 text-xs font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
                      {savingDevice ? "Speichern…" : "Übertragen"}
                    </button>
                    <button onClick={() => setEditingDevice(null)} className="px-4 rounded-full border border-white/15 text-[#ccc] text-xs hover:text-white transition-colors">Abbrechen</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
